<template>
  <GenericCard
    :locked="locked"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="v$.section.$invalid"
    :dirty="v$.section.$anyDirty"
    :create="create"
    title="Travel History"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-expansion-panels
        multiple
        tile
      >
        <v-expansion-panel class="my-2">
          <v-expansion-panel-header>
            <b>Entry</b>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col
                cols="8"
              >
                <FormatSelectableComboDate
                  class="mt-3 ml-1"
                  label="Entry Date"
                  :formatable-date="section.entry_date"
                  :format="section.entry_date_format"
                  :uncertain="section.entry_date_uncertain"
                  required
                  outlined
                  :disabled="locked"
                  for-party-edit
                  :error-messages="getErrorMessages(v$.section.entry_date)"
                  @update-box="onEntryDateUncertainUpdate"
                  @update-date="onEntryDateUpdate"
                  @tab-out="v$.section.entry_date.$touch()"
                />
              </v-col>
              <v-col cols="3">
                <v-autocomplete
                  v-model="section.immigration_status"
                  :items="optionsImmigrationStatuses"
                  label="Status at Entry"
                  outlined
                  :error-messages="getErrorMessages(v$.section.immigration_status)"
                  @input="v$.section.immigration_status.$touch()"
                  @blur="v$.section.immigration_status.$touch()"
                />
              </v-col>
              <v-col
                v-if="fieldRules.immigration_status_other"
              >
                <v-text-field
                  v-model="section.immigration_status_other"
                  autocomplete="off"
                  label="Immigration Status Other"
                  outlined
                  :error-messages="getErrorMessages(v$.section.immigration_status_other)"
                  @input="v$.section.immigration_status_other.$touch()"
                  @blur="v$.section.immigration_status_other.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                cols="4"
              >
                <v-autocomplete
                  v-model="section.entry_state"
                  :items="optionsEntryStates"
                  label="State"
                  outlined
                  :error-messages="getErrorMessages(v$.section.entry_state)"
                  @input="v$.section.entry_state.$touch()"
                  @blur="v$.section.entry_state.$touch()"
                />
              </v-col>
              <v-col
                cols="4"
              >
                <v-row>
                  <v-col>
                    <v-text-field
                      v-model="section.entry_place"
                      autocomplete="off"
                      label="Place of Entry"
                      outlined
                      :disabled="section.entry_place_unknown"
                      :error-messages="getErrorMessages(v$.section.entry_place)"
                      @input="v$.section.entry_place.$touch()"
                      @blur="v$.section.entry_place.$touch()"
                    />
                  </v-col>
                  <v-col cols="auto">
                    <v-checkbox
                      v-model="section.entry_place_unknown"
                      label="Unknown"
                      class="mt-3"
                      @input="v$.section.entry_place_unknown.$touch()"
                      @blur="v$.section.entry_place_unknown.$touch()"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col
                v-if="section.entry_place_unknown"
              >
                <v-text-field
                  v-model="section.reason_entry_unknown"
                  autocomplete="off"
                  label="Reason Entry Unknown"
                  outlined
                  :error-messages="getErrorMessages(v$.section.reason_entry_unknown)"
                  @input="v$.section.reason_entry_unknown.$touch()"
                  @blur="v$.section.reason_entry_unknown.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-autocomplete
                  v-model="section.entry_via"
                  :items="optionsEntryVia"
                  label="Entry Via"
                  outlined
                  :error-messages="getErrorMessages(v$.section.entry_via)"
                  @input="v$.section.entry_via.$touch()"
                  @blur="v$.section.entry_via.$touch()"
                />
              </v-col>
              <v-col>
                <v-autocomplete
                  v-model="section.entry_manner"
                  :items="optionsEntryManners"
                  label="Entry Manner"
                  outlined
                  :error-messages="getErrorMessages(v$.section.entry_manner)"
                  @input="v$.section.entry_manner.$touch()"
                  @blur="v$.section.entry_manner.$touch()"
                />
              </v-col>
              <v-col>
                <v-autocomplete
                  v-model="section.entry_documents"
                  :items="optionsEntryDocuments"
                  label="Entry Documents"
                  outlined
                  :error-messages="getErrorMessages(v$.section.entry_documents)"
                  @input="v$.section.entry_documents.$touch()"
                  @blur="v$.section.entry_documents.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-textarea
                  v-model="section.entry_manner_notes"
                  label="Entry Manner Notes"
                  rows="4"
                  outlined
                  :error-messages="getErrorMessages(v$.section.entry_manner_notes)"
                  @input="v$.section.entry_manner_notes.$touch()"
                  @blur="v$.section.entry_manner_notes.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-row>
                  <v-col cols="auto">
                    <label for="entry_apprehended">Apprehended on Entry?</label>
                    <v-radio-group
                      id="entry_apprehended"
                      v-model="section.entry_apprehended"
                      row
                      :error-messages="getErrorMessages(v$.section.entry_apprehended)"
                      class="mt-0"
                      @input="v$.section.entry_apprehended.$touch()"
                      @blur="v$.section.entry_apprehended.$touch()"
                    >
                      <v-radio
                        label="No"
                        :value="false"
                      />
                      <v-radio
                        label="Yes"
                        :value="true"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
                <v-row v-if="section.entry_apprehended">
                  <v-col>
                    <v-autocomplete
                      v-model="section.what_happened_entry"
                      :items="optionsWhatHappenedEntry"
                      label="What Happened"
                      outlined
                      :error-messages="getErrorMessages(v$.section.what_happened_entry)"
                      @input="v$.section.what_happened_entry.$touch()"
                      @blur="v$.section.what_happened_entry.$touch()"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col v-if="section.entry_apprehended">
                <v-textarea
                  v-model="section.entry_apprehended_notes"
                  label="Entry Apprehension Notes"
                  rows="5"
                  outlined
                  :error-messages="getErrorMessages(v$.section.entry_apprehended_notes)"
                  @input="v$.section.entry_apprehended_notes.$touch()"
                  @blur="v$.section.entry_apprehended_notes.$touch()"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel class="my-2">
          <v-expansion-panel-header>
            <b>Departure</b>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col
                cols="8"
              >
                <FormatSelectableComboDate
                  class="mt-3 ml-1"
                  label="Departure Date"
                  :formatable-date="section.departure_date"
                  :format="section.departure_date_format"
                  :uncertain="section.departure_date_uncertain"
                  required
                  outlined
                  :disabled="locked"
                  for-party-edit
                  :error-messages="getErrorMessages(v$.section.departure_date)"
                  @update-box="onDepartureDateUncertainUpdate"
                  @update-date="onDepartureDateUpdate"
                  @tab-out="v$.section.departure_date.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="section.departure_place"
                  autocomplete="off"
                  label="Place of Departure"
                  outlined
                  :error-messages="getErrorMessages(v$.section.departure_place)"
                  @input="v$.section.departure_place.$touch()"
                  @blur="v$.section.departure_place.$touch()"
                />
              </v-col>
              <v-col>
                <v-autocomplete
                  v-model="section.departure_via"
                  :items="optionsDepartureVia"
                  label="Departure Via"
                  outlined
                  :error-messages="getErrorMessages(v$.section.departure_via)"
                  @input="v$.section.departure_via.$touch()"
                  @blur="v$.section.departure_via.$touch()"
                />
              </v-col>
              <v-col>
                <v-text-field
                  v-model="section.travel_duration"
                  autocomplete="off"
                  label="Travel Duration"
                  outlined
                  :error-messages="getErrorMessages(v$.section.travel_duration)"
                  @input="v$.section.travel_duration.$touch()"
                  @blur="v$.section.travel_duration.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-row>
                  <v-col cols="auto">
                    <label for="departure_apprehended">Apprehended on Departure?</label>
                    <v-radio-group
                      id="departure_apprehended"
                      v-model="section.departure_apprehended"
                      row
                      :error-messages="getErrorMessages(v$.section.departure_apprehended)"
                      class="mt-0"
                      @input="v$.section.departure_apprehended.$touch()"
                      @blur="v$.section.departure_apprehended.$touch()"
                    >
                      <v-radio
                        label="No"
                        :value="false"
                      />
                      <v-radio
                        label="Yes"
                        :value="true"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>
                <v-row v-if="section.departure_apprehended">
                  <v-col>
                    <v-autocomplete
                      v-model="section.what_happened_departure"
                      :items="optionsWhatHappenedDeparture"
                      label="What Happened"
                      outlined
                      :error-messages="getErrorMessages(v$.section.what_happened_departure)"
                      @input="v$.section.what_happened_departure.$touch()"
                      @blur="v$.section.what_happened_departure.$touch()"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col v-if="section.departure_apprehended">
                <v-textarea
                  v-model="section.departure_apprehended_notes"
                  label="Departure Apprehension Notes"
                  rows="5"
                  outlined
                  :error-messages="getErrorMessages(v$.section.departure_apprehended_notes)"
                  @input="v$.section.departure_apprehended_notes.$touch()"
                  @blur="v$.section.departure_apprehended_notes.$touch()"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-row class="mt-4">
        <v-col>
          <v-textarea
            v-model="section.notes"
            label="Notes"
            rows="4"
            outlined
            :error-messages="getErrorMessages(v$.section.notes)"
            @input="v$.section.notes.$touch()"
            @blur="v$.section.notes.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { alertHandler } from '@/alertHandler'

import FormatSelectableComboDate from '@/components/Fields/FormatSelectableComboDate'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'
import { immigrationStatusReqChecker } from '@/store/party/partials/party-edit/helpers/pe-immigration-status-card'

export default {
  name: 'TravelHistoriesCard',
  components: { FormatSelectableComboDate, GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      section: {},
    }
  },

  validations () {
    return {
      section: {
        departure_apprehended: {},
        departure_apprehended_notes: {},
        departure_date: {},
        departure_date_uncertain: {},
        departure_place: {},
        departure_via: {},
        entry_apprehended: {},
        entry_apprehended_notes: {},
        entry_date: {},
        entry_date_uncertain: {},
        entry_documents: {},
        entry_manner: {},
        entry_manner_notes: {},
        entry_place: {},
        entry_place_unknown: {},
        entry_state: {},
        entry_via: {},
        immigration_status: {},
        immigration_status_other: {},
        notes: {},
        reason_entry_unknown: {},
        travel_duration: {},
        what_happened_departure: {},
        what_happened_entry: {},
      },
    }
  },

  computed: {
    cardIdentifier () { return `travel-histories-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },
    fieldRules () { return immigrationStatusReqChecker(this.section.immigration_status) },

    locked: {
      get () {
        return this.peTravelHistoriesCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeTravelHistoriesCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    optionsDepartureVia () {
      return this.getCachedDropdown('travel_histories/departure_via')
    },

    optionsEntryDocuments () {
      return this.getCachedDropdown('travel_histories/entry_documents')
    },

    optionsEntryManners () {
      return this.getCachedDropdown('travel_histories/entry_manner')
    },

    optionsEntryStates () {
      return this.getCachedDropdown('us_state/states')
    },

    optionsEntryVia () {
      return this.getCachedDropdown('travel_histories/entry_via')
    },

    optionsImmigrationStatuses () {
      return this.getCachedDropdown('travel_histories/immigration_status')
    },

    optionsWhatHappenedDeparture () {
      return this.getCachedDropdown('travel_histories/what_happened_departure')
    },

    optionsWhatHappenedEntry () {
      return this.getCachedDropdown('travel_histories/what_happened_entry')
    },

    sectionIdentifier () { return this.section.id || 'new' },

    ...mapGetters(PARTY_NAMESPACE, [
      'peTravelHistoriesCardLock',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPeTravelHistoriesCard(this.sectionIdentifier)

    this.getDropdownOptions({ path: 'travel_histories/immigration_status' })
    this.getDropdownOptions({ path: 'us_state/states' })
    this.getDropdownOptions({ path: 'travel_histories/entry_via' })
    this.getDropdownOptions({ path: 'travel_histories/departure_via' })
    this.getDropdownOptions({ path: 'travel_histories/entry_documents' })
    this.getDropdownOptions({ path: 'travel_histories/entry_manner' })
    this.getDropdownOptions({ path: 'travel_histories/what_happened_entry' })
    this.getDropdownOptions({ path: 'travel_histories/what_happened_departure' })
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peTravelHistoriesCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peTravelHistoriesCardCreateUndo()
      else {
        return this.peTravelHistoriesCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peTravelHistoriesCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peTravelHistoriesCardUpdate({
        id: this.section.id,
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    onDepartureDateUncertainUpdate (value) {
      this.v$.section.departure_date.$touch()
      this.v$.section.departure_date_uncertain.$touch()
      this.section.departure_date_uncertain = value
    },

    onDepartureDateUpdate (value) {
      this.v$.section.departure_date.$touch()
      this.v$.section.departure_date_uncertain.$touch()
      this.section.departure_date = value.date
      this.section.departure_date_format = value.format
    },

    onEntryDateUncertainUpdate (value) {
      this.v$.section.entry_date.$touch()
      this.v$.section.entry_date_uncertain.$touch()
      this.section.entry_date_uncertain = value
    },

    onEntryDateUpdate (value) {
      this.v$.section.entry_date.$touch()
      this.v$.section.entry_date_uncertain.$touch()
      this.section.entry_date = value.date
      this.section.entry_date_format = value.format
    },

    async onSectionUnlock () {
      return this.setPeTravelHistoriesCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peTravelHistoriesCardCreateUndo',
      'setPeTravelHistoriesCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeTravelHistoriesCard',
      'peTravelHistoriesCardCreate',
      'peTravelHistoriesCardDelete',
      'peTravelHistoriesCardRefresh',
      'peTravelHistoriesCardUpdate',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
